/**
 * RestoID — MFA factor picker (binding).
 * ---------------------------------------------------------------------------
 * RestoID frame around `MfaFactorRows` (RestoID OptionRows that mirror upstream
 * `MfaFactorList.handleSelectFactor` 1:1) + the PRISTINE upstream skip logic
 * (`useSkipMfa` / `useSkipOptionalMfa`). Guard mirrors the upstream page.
 * Swapped in via `src/App.tsx`.
 */
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import useMfaFlowState from '@/hooks/use-mfa-factors-state';
import useSkipMfa from '@/hooks/use-skip-mfa';
import useSkipOptionalMfa from '@/hooks/use-skip-optional-mfa';
import { UserMfaFlow } from '@/types';

import MfaFactorRows from '../components/MfaFactorRows';
import { GhostAction, ScreenHead, Stack } from '../components/restoid';

import ErrorPage from './ErrorPage';

const MfaBinding = () => {
  const { t } = useTranslation();
  const flowState = useMfaFlowState();
  const skipMfa = useSkipMfa();
  const skipOptionalMfa = useSkipOptionalMfa();

  if (!flowState) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const onSkip = flowState.skippable
    ? flowState.suggestion
      ? skipOptionalMfa
      : skipMfa
    : undefined;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.add_mfa_factors' }}>
      <Stack gap={20}>
        <ScreenHead
          title={t(flowState.suggestion ? 'mfa.add_another_mfa_factor' : 'mfa.add_mfa_factors')}
          sub={t(
            flowState.suggestion ? 'mfa.add_another_mfa_description' : 'mfa.add_mfa_description'
          )}
        />
        <MfaFactorRows flow={UserMfaFlow.MfaBinding} flowState={flowState} />
        {onSkip ? <GhostAction onClick={() => onSkip()}>{t('action.nav_skip')}</GhostAction> : null}
      </Stack>
    </FirstScreenLayout>
  );
};

export default MfaBinding;
